import { ForbiddenError } from '../../errors/ForbiddenError';
import { ERROR_TABLE_BLOCKED_FOR_COUNTRY_CODE, ForbiddenErrors } from '../../errors/types';

import type {
  IServerTableConfigResponse,
  ParseBetLimitsType,
  ParsePayoutsType,
  PayoutType,
  TableConfigType,
} from './types';
import { WinMessageBranding } from './types';
import { getJurisdictionData } from './utils';

export const chipColorCodesNormalizer = (colorCodes: string | string[] | undefined): string[] => {
  if (typeof colorCodes === 'string') {
    return colorCodes.split(',');
  }

  return Array.isArray(colorCodes) ? colorCodes : [];
};

export const defaultParsePayouts = (betCode: IServerTableConfigResponse['betCode']): PayoutType[] => {
  if (!betCode) {
    return [];
  } else {
    const bets = Object.keys(betCode);
    const payouts: PayoutType[] = [];
    bets.forEach((bet) => {
      if (betCode[bet] !== undefined) {
        payouts.push({
          code: parseInt(bet),
          payout: Number(betCode[bet]),
        });
      }
    });
    return payouts;
  }
};

export const tableConfigParser = ({
  response,
  parseBetLimits,
  parsePayouts,
}: {
  response: IServerTableConfigResponse;
  parseBetLimits: ParseBetLimitsType;
  parsePayouts?: ParsePayoutsType;
}): TableConfigType => {
  if (response.params?.errorCode?.toString() === ERROR_TABLE_BLOCKED_FOR_COUNTRY_CODE) {
    throw new ForbiddenError(ForbiddenErrors.TABLE_BLOCKED_FOR_COUNTRY);
  }
  if (!response.params || !response.betCode || !response.operatorGameId || !response.tableId || !response.tableName) {
    throw new Error('Table config does not have required data');
  }
  try {
    return {
      chipColorCodes: chipColorCodesNormalizer(response.params.chip_color_codes),
      defaultChipOrder: response.params.default_chip_order,
      chipAmounts: response.params.chip_amounts
        ? response.params.chip_amounts.split(',').map((el: string) => Number.parseFloat(el))
        : [],

      betCode: response.betCode,

      gsAddress: response.params.gs_address,
      wsAddress: response.params.ws_address,

      chatDisabled: response.params.chatDisabled,
      chatVisibleByDefault: response.params.chatVisibleByDefault,

      betLimits: parseBetLimits(response.params),
      tableBetLimit: {
        max: response.params.table_bet_max_limit,
        min: response.params.table_bet_min_limit,
      },

      tableClosed: response.params.table_closed,
      tableName: response.tableName,
      tableType: response.params.table_type,
      tableVariant: response.params.tableVariant,

      winMessageBranding: response.params.youwinfeature ?? WinMessageBranding.ONLY_PP_BRANDING,
      showPPBranding: response.params.show_pp_branding ?? true,
      hideLobby: response.params.hide_lobby,
      isInfiniteAutoPlayEnabled: response.params.isInfiniteAutoPlayEnabled,
      tableCategory: response.params.table_category
        ? response.params.table_category.split(',').map((tableType) => tableType.trim())
        : [],

      operatorGameId: response.operatorGameId,
      operatorTheme: response.params.operator_theme,
      idleLimit: response.params.idle_limit_games,
      openTime: response.params.openTime,
      payoutBetMaxLimit: response.params.payout_bet_max_limit,
      euroTablePayoutMax: response.params.euro_table_payout_max,
      maxMultiplier: response.params.maxMultiplier,
      payouts: parsePayouts ? parsePayouts(response.params) : defaultParsePayouts(response.betCode),
      defaultBetAmount: response.params.default_bet_amount,
      jurisdiction: getJurisdictionData(response.params.jurisdiction_functionalities),
      dedicatedLanguage: response.params.dedicatedLanguage,
      isAutomatedMessagesEnabled: response.params.automated_messages_enabled,
      subtitlesAvailable: response.params.subtitles_available === true,
      audioMode: response.params.audioMode ?? null,
    };
  } catch {
    throw new Error('Something is missing in table config');
  }
};
